import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, CalendarCheck, CalendarX, Loader2, Clock, Landmark } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Huwelijk",
  "Verjaardag",
  "Communie / lentefeest",
  "Bedrijfsfeest",
  "Babyborrel",
  "Jubileum",
  "Ander feest",
];

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const parseKey = (k) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const DEPOSIT = "€150";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  date: "",
  start_time: "20:00",
  hours: 4,
  event_type: EVENT_TYPES[0],
  location: "",
  guests: "",
  notes: "",
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow block mb-2 text-neutral-500">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent text-ink";

const MagicMirrorReservation = () => {
  const [form, setForm] = useState(emptyForm);
  const [terms, setTerms] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [booked, setBooked] = useState([]);
  const [pending, setPending] = useState([]);
  const [blocked, setBlocked] = useState([]);

  useEffect(() => {
    const key = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
    axios
      .get(`${API}/magic-mirror/availability`, { params: { month: key } })
      .then(({ data }) => {
        setBooked(data.booked || []);
        setPending(data.pending || []);
        setBlocked(data.blocked || []);
      })
      .catch(() => {});
  }, [month]);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSelectDate = async (day) => {
    if (!day) return;
    const date = toKey(day);
    setForm((s) => ({ ...s, date }));
    setAvailability(null);
    setChecking(true);
    try {
      const { data } = await axios.get(`${API}/magic-mirror/availability/${date}`);
      setAvailability(data);
    } catch {
      setAvailability(null);
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) {
      toast.error("Vink eerst de verhuurvoorwaarden aan.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        hours: Number(form.hours),
        guests: form.guests ? Number(form.guests) : null,
        notes: form.notes || null,
        terms_accepted: true,
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/magic-mirror/reservations`, payload);
      setDone(data);
      toast.success("Aanvraag verzonden — check je inbox voor de bevestiging.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section id="reserveren" className="bg-ink text-white py-24 md:py-32 print-hide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="container-narrow max-w-2xl text-center"
          data-testid="mm-reservation-success"
        >
          <CalendarCheck size={34} className="text-gold mx-auto" strokeWidth={1.3} />
          <h2 className="font-serif text-4xl md:text-5xl mt-8 leading-tight">
            Je aanvraag is <span className="italic text-gold">onderweg.</span>
          </h2>
          <p className="mt-6 text-white/70 leading-relaxed">
            Bedankt {done.name}. Ik bekijk je aanvraag voor{" "}
            <span className="text-gold">{done.date}</span> en laat je zo snel mogelijk weten of je
            datum goedgekeurd is. Daarna ontvang je de betaalgegevens voor het voorschot van{" "}
            {DEPOSIT}. De bevestigingsmail met de volledige verhuurvoorwaarden staat al in je inbox.
          </p>
          <button
            type="button"
            onClick={() => {
              setDone(null);
              setForm(emptyForm);
              setTerms(false);
              setAvailability(null);
            }}
            data-testid="mm-reservation-new-btn"
            className="btn-outline-light mt-10"
          >
            Nieuwe aanvraag
          </button>
        </motion.div>
      </section>
    );
  }

  const dateTaken = availability && availability.available === false;

  return (
    <section id="reserveren" className="bg-white py-24 md:py-32 print-hide">
      <div className="container-narrow max-w-3xl">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-3">Reserveren</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">
            Check je datum &amp; <span className="italic text-gold">reserveer.</span>
          </h2>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            Kies je datum en zie meteen of de Magic Mirror nog vrij is. Je aanvraag is vrijblijvend
            — ik bevestig persoonlijk per e-mail.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-8" data-testid="mm-reservation-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Naam">
              <input
                data-testid="mm-name"
                required
                minLength={2}
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Doe"
                className={inputCls}
              />
            </Field>
            <Field label="E-mail">
              <input
                data-testid="mm-email"
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="jane@voorbeeld.be"
                className={inputCls}
              />
            </Field>
            <Field label="Telefoon">
              <input
                data-testid="mm-phone"
                required
                minLength={6}
                value={form.phone}
                onChange={update("phone")}
                placeholder="0495 61 05 96"
                className={inputCls}
              />
            </Field>
            <Field label="Type feest">
              <select
                data-testid="mm-event-type"
                value={form.event_type}
                onChange={update("event_type")}
                className={`${inputCls} appearance-none`}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Date + availability */}
          <div className="bg-beige p-6 md:p-8 border-l-2 border-gold">
            <p className="eyebrow mb-5 text-neutral-500">Kies je datum</p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7">
                <div className="bg-white p-2 sm:p-4 inline-block" data-testid="mm-calendar">
                  <Calendar
                    mode="single"
                    selected={form.date ? parseKey(form.date) : undefined}
                    onSelect={onSelectDate}
                    month={month}
                    onMonthChange={setMonth}
                    fromDate={new Date()}
                    disabled={[
                      { before: new Date() },
                      ...booked.map(parseKey),
                      ...blocked.map(parseKey),
                    ]}
                    modifiers={{ pendingDay: pending.map(parseKey) }}
                    modifiersClassNames={{
                      pendingDay: "underline decoration-gold decoration-2 underline-offset-4",
                    }}
                    classNames={{
                      day_selected:
                        "bg-gold text-white hover:bg-gold hover:text-white focus:bg-gold focus:text-white",
                      day_disabled:
                        "text-neutral-300 line-through opacity-100 hover:bg-transparent cursor-not-allowed",
                    }}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-xs text-neutral-500">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 bg-gold inline-block" /> Gekozen
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="text-neutral-300 line-through">15</span> Niet beschikbaar
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="underline decoration-gold decoration-2 underline-offset-4">15</span>{" "}
                    Aanvraag lopende
                  </span>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-6">
                <Field label="Gekozen datum">
                  <input
                    data-testid="mm-date"
                    required
                    readOnly
                    value={form.date}
                    placeholder="Klik een dag in de kalender"
                    className={`${inputCls} cursor-default`}
                  />
                </Field>
                <Field label="Startuur">
                  <input
                    data-testid="mm-start-time"
                    required
                    type="time"
                    value={form.start_time}
                    onChange={update("start_time")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Aantal uren">
                  <select
                    data-testid="mm-hours"
                    value={form.hours}
                    onChange={update("hours")}
                    className={`${inputCls} appearance-none`}
                  >
                    {[2, 3, 4, 5, 6, 7, 8].map((h) => (
                      <option key={h} value={h}>
                        {h} uur
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>

            <div className="mt-6 min-h-[24px] text-sm" data-testid="mm-availability-status">
              {checking && (
                <span className="inline-flex items-center gap-2 text-neutral-500">
                  <Loader2 size={14} className="animate-spin" />
                  Beschikbaarheid controleren...
                </span>
              )}
              {!checking && availability?.status === "free" && (
                <span className="inline-flex items-center gap-2 text-gold">
                  <CalendarCheck size={15} />
                  Goed nieuws — deze datum is nog vrij.
                </span>
              )}
              {!checking && availability?.status === "pending" && (
                <span className="inline-flex items-center gap-2 text-ink/70">
                  <Clock size={15} className="text-gold" />
                  Er loopt al een aanvraag voor deze datum — wie eerst bevestigt, krijgt de datum.
                </span>
              )}
              {!checking && dateTaken && (
                <span className="inline-flex items-center gap-2 text-red-700">
                  <CalendarX size={15} />
                  {availability?.status === "blocked"
                    ? "Deze datum is niet beschikbaar. Kies een andere datum."
                    : "Deze datum is al volzet. Kies een andere datum."}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Locatie / adres">
              <input
                data-testid="mm-location"
                required
                minLength={3}
                value={form.location}
                onChange={update("location")}
                placeholder="Feestzaal De Linde, Beveren"
                className={inputCls}
              />
            </Field>
            <Field label="Aantal gasten (optioneel)">
              <input
                data-testid="mm-guests"
                type="number"
                min={1}
                max={2000}
                value={form.guests}
                onChange={update("guests")}
                placeholder="120"
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Extra wensen (optioneel)">
            <textarea
              data-testid="mm-notes"
              rows={3}
              maxLength={1000}
              value={form.notes}
              onChange={update("notes")}
              placeholder="Eigen fotokader met onze namen, gouden accessoires, ..."
              className={`${inputCls} resize-none`}
            />
          </Field>

          {/* Terms checkbox */}
          <label className="flex items-start gap-4 cursor-pointer pt-2">
            <input
              data-testid="mm-terms-checkbox"
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              className="mt-1 h-4 w-4 accent-gold shrink-0"
            />
            <span className="text-sm text-neutral-700 leading-relaxed">
              Ik heb de{" "}
              <Link
                to="/verhuurvoorwaarden-magic-mirror"
                target="_blank"
                className="text-gold hover:underline"
                data-testid="mm-terms-link"
              >
                verhuurvoorwaarden
              </Link>{" "}
              gelezen en ga ermee akkoord. Je ontvangt ze ook in je bevestigingsmail.
            </span>
          </label>

          {/* Payment info */}
          <div className="bg-ink text-white p-6 md:p-8 relative" data-testid="mm-payment-info">
            <div className="flex items-start gap-4">
              <Landmark size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.4} />
              <div className="space-y-3 text-sm leading-relaxed">
                <p className="font-serif text-lg text-white">Zo verloopt de betaling</p>
                <p className="text-white/70">
                  Je aanvraag is <span className="text-gold">vrijblijvend</span>. Na goedkeuring
                  ontvang je een e-mail met de betaalgegevens voor een voorschot van{" "}
                  <span className="text-gold">{DEPOSIT}</span> via overschrijving — binnen 7 dagen te
                  betalen om je datum te behouden. Het resterende bedrag betaal je op de dag van het
                  feest.
                </p>
                <p className="text-white/50 text-[13px]">
                  De schadewaarborg van €300 betaal je niet online: die breng je cash mee of schrijf
                  je over op de dag van het feest, en wordt volledig terugbetaald wanneer er geen
                  schade of verlies is.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-6 border-t border-neutral-200">
            <div className="flex items-center gap-3 text-sm text-neutral-600">
              <Check size={16} className="text-gold" />
              <span>Vrijblijvend — pas definitief na jouw voorschot.</span>
            </div>
            <button
              data-testid="mm-reservation-submit"
              type="submit"
              disabled={loading || !terms || dateTaken}
              className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Versturen..." : "Verstuur aanvraag"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default MagicMirrorReservation;
